import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { reviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

const image1 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample1.png`;
//const image2 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample2.png`;
const image3 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/GoogleSheets2020Logo.svg`;

function ImageCarousel({ images }) {
    const [idx, setIdx] = React.useState(0);
  
    React.useEffect(() => {
      if (!Array.isArray(images)) return;
  
      images.forEach((img) => {
        const preloader = new Image();
        preloader.src = img.image;
      });
    }, [images]);
  
    if (!images || images.length === 0) return null;
  
    const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
    const next = () => setIdx((i) => (i + 1) % images.length);
  
    return (
      <Paper className="bws-gsl-carousel" elevation={0}>
        <div className="bws-gsl-carouselInner">
          <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
            <ChevronLeftIcon />
          </IconButton>
  
          <div className="bws-gsl-carouselFrame">
            {images.map((img, i) => (
              <img
                key={i}
                className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
                src={img.image}
                alt={img.altText}
                loading="eager"
                decoding="async"
              />
            ))}
          </div>
  
          <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
            <ChevronRightIcon />
          </IconButton>
        </div>
  
        <div className="bws-gsl-carouselDots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
              aria-label={`View example ${i + 1}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      </Paper>
    );
  }

export default function GoogleSheetsExpert() {
  const safeReviews = Array.isArray(reviews) ? reviews : [];
  const title = "Google Sheets Expert — Formulas, Dashboards, Automation & Cleanup";
  const description =
    "Need a Google Sheets expert? Get help with formulas, dashboards, Apps Script automation, reporting, cleanup, and workflow improvements.";
  const canonical = "https://brainwavesolutions.net/services/google-sheets-expert";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Google Sheets Expert", url: canonical },
    ]),
    buildServiceSchema({
      name: "Google Sheets Expert",
      description,
      canonical,
      offerLabel: "$50+",
      serviceType: "Google Sheets support and Apps Script automation",
    }),
  ];

  const images = [
    {
      image: image1,
      altText:
        "Google Sheets financial dashboard showing income vs expenses chart, earnings split table, and income and expense transaction logs for a client budget summary.",
    },
    {
      image: image3,
      altText:
        "Google Sheets cost tracking spreadsheet showing labor, meals, lodging, and expense totals across multiple days.",
    },
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Google Sheets expert help — fast, practical, and reliable
              </Typography>

              <p className="bws-gsl-lead">
                If your Google Sheet is giving you bad numbers, breaking when data changes, or taking way too much time
                to manage every week, you are not alone. Google Sheets is powerful, but once your project begins to grow, it can become frustrating fast.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals fix formulas, clean up messy spreadsheets, build dashboards, and
                automate recurring work in Google Sheets — so the sheet becomes something useful instead of something
                everyone dreads opening.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Google Sheets formulas</span>
                <span className="bws-gsl-badge">Dashboards & reporting</span>
                <span className="bws-gsl-badge">Apps Script automation</span>
                <span className="bws-gsl-badge">Spreadsheet cleanup</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with Google Sheets
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses & individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common Google Sheets problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Broken formulas, bad references, and mismatched totals</li>
                  <li>QUERY, FILTER, ARRAYFORMULA, XLOOKUP, SUMIFS, pivots, and validation issues</li>
                  <li>Messy shared sheets that are hard to trust</li>
                  <li>Manual reporting that should be automated</li>
                  <li>Apps Script workflows for recurring tasks</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with a description of your project, and how I can be of assistance. I will reply ASAP, typically within 24 hours.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Fix what’s broken</Typography>
              <Typography className="bws-gsl-stripText">
                Wrong totals, broken formulas, inconsistent results, and sheets that feel fragile.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make it easier to use</Typography>
              <Typography className="bws-gsl-stripText">
                Better structure, clearer inputs, cleaner summaries, and less chance of user error.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Save time every week</Typography>
              <Typography className="bws-gsl-stripText">
                Dashboards, rollups, and Apps Script automations that cut down repetitive work.
              </Typography>
            </Paper>
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-sectionHead">
              <Typography className="bws-gsl-h2">Examples:</Typography>
              <p className="bws-gsl-p">
                Dashboard and reporting examples built in Google Sheets.
              </p>
            </div>

            <ImageCarousel images={images} />
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Google Sheets formulas and logic fixes</li>
                    <li>Dashboards, trackers, and summary sheets</li>
                    <li>Data cleanup and spreadsheet restructuring</li>
                    <li>Apps Script automation for recurring workflows</li>
                    <li>Reporting and rollups that are easier to trust</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Business owners who rely on Google Workspace</li>
                    <li>Teams sharing messy operational sheets</li>
                    <li>People stuck on one spreadsheet issue they can’t solve</li>
                    <li>Reporting, scheduling, inventory, quoting, and budgeting workflows</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-sectionHead">
              <Typography className="bws-gsl-h2">Client feedback</Typography>
            </div>

            <div className="bws-gsl-reviews">
              {safeReviews.slice(0, 3).map((r, idx) => (
                <Paper key={idx} className="bws-gsl-review" elevation={0}>
                  <Rating
                    value={5}
                    readOnly
                    size="small"
                    className="bws-gsl-reviewStars"
                    aria-label="5 out of 5 stars"
                  />
                  <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                  <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                </Paper>
              ))}
            </div>
          </div>

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you fix just one Google Sheets formula?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Some projects are quick fixes, while others involve a full cleanup, dashboard build, or workflow
                  redesign.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you automate Google Sheets?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. I can use Google Apps Script to automate recurring steps, reporting, updates, and other routine
                  spreadsheet work.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the problem and the result you want. A screenshot or sample sheet helps a lot.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help with Google Sheets?</Typography>
              <p className="bws-muted">
                Send a quick description of what’s not working and what you want the sheet to do.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
